/**
 * TaskCard - Individual task card for Kanban board
 */
import React from 'react';
import { Draggable } from '@hello-pangea/dnd';
import { Task, TASK_TYPE_LABELS, PRIORITY_COLORS } from '../../types/task';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { format } from 'date-fns';
import { sk } from 'date-fns/locale';

interface TaskCardProps {
  task: Task;
  index: number;
  onClick: () => void;
}

export const TaskCard: React.FC<TaskCardProps> = ({ task, index, onClick }) => {
  const completedChecklist = task.checklist.filter((item) => item.completed).length;
  const totalChecklist = task.checklist.length;
  const checklistProgress = totalChecklist > 0 ? (completedChecklist / totalChecklist) * 100 : 0;

  const priorityColor = PRIORITY_COLORS[task.priority] || PRIORITY_COLORS[0];

  return (
    <Draggable draggableId={`task-${task.id}`} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onClick={onClick}
          className={`task-card priority-${task.priority} ${snapshot.isDragging ? 'dragging' : ''}`}
        >
          {/* Task header */}
          <div className="task-card-header">
            <div className="task-card-title">{task.title}</div>
            <div className="task-card-type">{TASK_TYPE_LABELS[task.task_type]}</div>
          </div>

          {/* Description */}
          {task.description && (
            <div className="task-card-description">{task.description}</div>
          )}

          {/* Meta info */}
          {(task.pole_name || task.cable_name || task.onu_name || task.deadline) && (
            <div className="task-card-meta">
              {task.pole_name && (
                <span className="task-card-badge">
                  📍 {task.pole_name}
                </span>
              )}
              {task.cable_name && (
                <span className="task-card-badge">
                  📏 {task.cable_name}
                </span>
              )}
              {task.onu_name && (
                <span className="task-card-badge">
                  🔌 {task.onu_name}
                </span>
              )}
              {task.deadline && (
                <span className="task-card-badge">
                  ⏰ {format(new Date(task.deadline), 'dd.MM.yyyy', { locale: sk })}
                </span>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="task-card-footer">
            {/* Checklist progress */}
            {totalChecklist > 0 && (
              <div className="task-card-progress">
                <div style={{ width: 40, height: 40 }}>
                  <CircularProgressbar
                    value={checklistProgress}
                    text={`${completedChecklist}/${totalChecklist}`}
                    styles={buildStyles({
                      textSize: '32px',
                      pathColor: checklistProgress === 100 ? '#10b981' : '#3b82f6',
                      textColor: '#374151',
                      trailColor: '#e5e7eb',
                    })}
                  />
                </div>
              </div>
            )}

            {/* Assigned to */}
            {task.assigned_to && (
              <div className="task-card-assigned">
                👤 {task.assigned_to}
              </div>
            )}
          </div>

          {/* Attachments count */}
          {task.attachments.length > 0 && (
            <div style={{ marginTop: 8, fontSize: 11, color: '#64748b' }}>
              📎 {task.attachments.length}
            </div>
          )}
        </div>
      )}
    </Draggable>
  );
};

